import React, { useState, useEffect } from 'react';
import { 
    FaShoppingBag, FaBoxes, FaUsers, FaRupeeSign, 
    FaTruck, FaCheckCircle, FaClock, FaSearch, FaStore 
} from 'react-icons/fa';
import apiCall from '../helper/apiCall';

const Home = () => {
    // Filter States
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dateFilter, setDateFilter] = useState('Today');
    const [statusFilter, setStatusFilter] = useState('All'); 
    const [searchQuery, setSearchQuery] = useState(''); 
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [userCount, setUserCount] = useState(0);

    // Fetch Logic
    useEffect(() => {
        const fetchOrders = async () => {
            setLoading(true);
            try {
                const response = await apiCall("getOrders", { 
                    dateFilter, 
                    startDate, 
                    endDate, 
                    statusFilter, 
                    searchQuery 
                });
                
                if (response?.success) {
                    setOrders(response?.data);
                } else {
                    setOrders([]);
                }
            } catch (error) {
                console.error("Failed to fetch orders:", error);
                setOrders([]);
            } finally {
                setLoading(false);
            }
        };
        fetchOrders();
    }, [dateFilter, startDate, endDate, statusFilter, searchQuery]);

    useEffect(() => {
        const fetchUserCount = async () => {
            try {
                const response = await apiCall("getUserCount");
                if (response?.success) {
                    setUserCount(response.data.count);
                }
            } catch (error) {
                console.error("Failed to fetch user count:", error);
            }
        };
        fetchUserCount();
    }, []);

    // Helper to map shipping_status_id to UI labels
    const getStatusUI = (id) => {
        switch (id) {
            case 10: return { label: 'Placed', color: 'text-blue-600 bg-blue-50', icon: <FaClock /> };
            case 11: return { label: 'Ready for Label', color: 'text-amber-600 bg-amber-50', icon: <FaBoxes /> };
            case 12: return { label: 'Shipped', color: 'text-purple-600 bg-purple-50', icon: <FaTruck /> };
            case 4:  return { label: 'Delivered', color: 'text-emerald-600 bg-emerald-50', icon: <FaCheckCircle /> };
            default: return { label: 'Pending', color: 'text-slate-400 bg-slate-50', icon: <FaClock /> };
        }
    };

    /**
     * Logic to determine which date to show based on the current milestone
     */
    const getDisplayDate = (order, statusLabel) => {
        const options = { day: '2-digit', month: 'short', year: 'numeric' };
        
        if (statusLabel === 'Delivered' && order.delivery_date) {
            return { 
                type: "Delivered", 
                date: new Date(order.delivery_date).toLocaleDateString('en-IN', options) 
            };
        }
        if (statusLabel === 'Shipped' && order.shipping_date) {
            return { 
                type: "Shipped", 
                date: new Date(order.shipping_date).toLocaleDateString('en-IN', options) 
            };
        }
        // Fallback to Order Date for Placed/Ready/Pending
        const fallbackDate = order.order_date ? new Date(order.order_date).toLocaleDateString('en-IN', options) : 'N/A';
        return { 
            type: "Ordered", 
            date: fallbackDate
        };
    };

    // Derived Stats
    const totalRevenue = orders.reduce((acc, curr) => {
        const val = curr.final_amount_INR ? curr.final_amount_INR.replace(/[^0-9.-]+/g, "") : 0;
        return acc + parseFloat(val);
    }, 0);

    const offlineOrders = orders.filter(o => o.order_number?.startsWith('OFF'));
    const onlineOrdersCount = orders.length - offlineOrders.length;

    return (
        <div className="p-4 md:p-6 space-y-8 bg-slate-50 min-h-screen animate-fadeIn">
            {/* Header & Filters */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-serif italic text-slate-900">Executive Dashboard</h1>
                    <p className="text-[10px] uppercase tracking-[0.3em] text-slate-400 font-bold mt-1">
                        Omnichannel Shipping Overview
                    </p>
                </div>
                
                <div className="flex flex-wrap gap-2">
                    <select 
                        value={dateFilter} 
                        onChange={(e) => setDateFilter(e.target.value)}
                        className="flex-1 md:flex-none px-3 py-1 bg-white border border-slate-200 rounded-sm text-[10px] font-bold uppercase tracking-widest text-slate-500 shadow-sm outline-none cursor-pointer hover:border-slate-400 transition-colors"
                    >
                        <option>Today</option>
                        <option>Yesterday</option>
                        <option>Last 7 Days</option>
                        <option>Last 30 Days</option>
                        <option>Last 6 Months</option>
                        <option>All Time</option>
                    </select>

                    <select 
                        value={statusFilter} 
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="flex-1 md:flex-none px-3 py-1 bg-white border border-slate-200 rounded-sm text-[10px] font-bold uppercase tracking-widest text-slate-500 shadow-sm outline-none cursor-pointer hover:border-slate-400 transition-colors"
                    >
                        <option value="All">All Status</option>
                        <option value="Order Placed">Order Placed</option>
                        <option value="Ready for shipping label.">Ready for Label</option>
                        <option value="The Package has been shipped.">Shipped</option>
                        <option value="Delivered">Delivered</option>
                    </select>
                </div>
            </div>

            {/* Stats Grid - Responsive Column Count */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
                {[
                    { label: "Total Revenue", value: `₹${totalRevenue.toLocaleString()}`, icon: <FaRupeeSign />, color: "bg-emerald-50 text-emerald-600" },
                    { label: "Online Orders", value: onlineOrdersCount, icon: <FaShoppingBag />, color: "bg-blue-50 text-blue-600" },
                    { label: "Offline Sales", value: offlineOrders.length, icon: <FaStore />, color: "bg-slate-100 text-slate-600" },
                    { label: "Awaiting Ship", value: orders.filter(o => o.shipping_status_id === 11).length, icon: <FaBoxes />, color: "bg-rose-50 text-rose-600" },
                    { label: "Total Units", value: orders.reduce((acc, o) => acc + (o.total_quanitiy || 0), 0), icon: <FaUsers />, color: "bg-amber-50 text-amber-600" },
                    { label: "Total Users", value: userCount, icon: <FaUsers />, color: "bg-amber-50 text-amber-600" },
                ].map((stat, i) => (
                    <div key={i} className="bg-white p-6 border border-slate-100 shadow-sm hover:shadow-md transition-all group">
                        <div className="flex justify-between items-start">
                            <div className={`p-3 rounded-sm ${stat.color} group-hover:bg-slate-900 group-hover:text-white transition-colors`}>
                                {stat.icon}
                            </div>
                        </div>
                        <div className="mt-4">
                            <h3 className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">{stat.label}</h3>
                            <p className="text-2xl font-serif text-slate-900 mt-1">{stat.value}</p>
                        </div>
                    </div>
                ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Recent Orders List */}
                <div className="lg:col-span-2 bg-white border border-slate-100 shadow-sm overflow-hidden flex flex-col">
                    <div className="p-4 border-b border-slate-50 flex flex-col sm:flex-row justify-between items-start sm:items-center bg-white sticky top-0 z-10 gap-3">
                        <h3 className="text-xs uppercase tracking-[0.2em] font-bold text-slate-800">Recent Shipping Activity</h3>
                        <div className="relative w-full sm:w-auto">
                            <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300 text-xs" />
                            <input 
                                type="text" 
                                placeholder="Search inventory..." 
                                className="w-full pl-8 pr-4 py-1 border border-slate-100 text-xs outline-none focus:border-amber-500 transition-colors md:w-64"
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                        </div>
                    </div>

                    <div className="divide-y divide-slate-50 overflow-y-auto max-h-[600px]">
                        {loading ? (
                            <div className="p-10 text-center text-slate-400 font-serif italic animate-pulse">Updating records...</div>
                        ) : orders.length > 0 ? (
                            orders.map((order, idx) => {
                                const ui = getStatusUI(order.shipping_status_id);
                                const isOffline = order.order_number?.startsWith('OFF');
                                const displayDate = getDisplayDate(order, ui.label);

                                return (
                                    <div key={idx} className="p-4 flex items-center justify-between hover:bg-slate-50/50 transition-colors">
                                        <div className="flex items-center gap-4">
                                            <div className={`w-10 h-10 rounded-full flex-shrink-0 flex items-center justify-center ${ui.color}`}>
                                                {ui.icon}
                                            </div>
                                            <div>
                                                <div className="flex items-center gap-2">
                                                    <p className="text-sm font-medium text-slate-800">{order.order_number}</p>
                                                    {isOffline && (
                                                        <span className="text-[8px] bg-slate-900 text-white px-1.5 py-0.5 rounded-full font-bold uppercase tracking-tighter">
                                                            Offline
                                                        </span>
                                                    )}
                                                </div>
                                                <p className="text-[10px] text-slate-400 uppercase tracking-widest mt-0.5">
                                                    {ui.label} • <span className="text-slate-600 font-semibold">{displayDate.type}: {displayDate.date}</span>
                                                </p>
                                            </div>
                                        </div>
                                        <div className="text-right flex-shrink-0">
                                            <p className="text-sm font-serif text-slate-900">{order.final_amount_INR}</p>
                                            <p className="text-[9px] text-slate-400 uppercase tracking-tighter">{order.total_quanitiy} Items</p>
                                        </div>
                                    </div>
                                );
                            })
                        ) : (
                            <div className="p-10 text-center text-slate-400">No transactions found for this selection.</div>
                        )}
                    </div>
                </div>

                {/* Right Column: Fulfillment Analytics */}
                <div className="space-y-6">
                    <div className="bg-slate-900 text-white p-6 rounded-sm shadow-xl flex flex-col justify-between h-full min-h-[350px]">
                        <div>
                            <h3 className="text-[10px] uppercase tracking-[0.3em] font-bold text-slate-400 mb-6">Fulfillment Health</h3>
                            <div className="space-y-6">
                                <div>
                                    <div className="flex justify-between text-[10px] uppercase tracking-widest mb-2">
                                        <span>Dispatch Success</span>
                                        <span>{orders.length > 0 ? '94%' : '0%'}</span>
                                    </div>
                                    <div className="w-full bg-slate-800 h-1 rounded-full overflow-hidden">
                                        <div className="bg-amber-500 h-full w-[94%] transition-all duration-700" />
                                    </div>
                                </div>
                                <div>
                                    <div className="flex justify-between text-[10px] uppercase tracking-widest mb-2">
                                        <span>Offline vs Online</span>
                                        <span>{orders.length > 0 ? `${Math.round((offlineOrders.length / orders.length) * 100)}%` : '0%'}</span>
                                    </div>
                                    <div className="w-full bg-slate-800 h-1 rounded-full overflow-hidden">
                                        <div 
                                            className="bg-blue-400 h-full transition-all duration-700" 
                                            style={{ width: orders.length > 0 ? `${(offlineOrders.length / orders.length) * 100}%` : '0%' }}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        <div className="space-y-3 mt-8">
                            <button className="w-full py-3 bg-white text-slate-900 text-[10px] font-bold uppercase tracking-[0.2em] hover:bg-amber-500 hover:text-white transition-all rounded-sm shadow-lg">
                                Generate Daily Report
                            </button>
                            <button className="w-full py-3 bg-slate-800 text-slate-300 text-[10px] font-bold uppercase tracking-[0.2em] hover:bg-slate-700 transition-all rounded-sm">
                                Export CSV
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Home;